import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
};

export default function TargetsAdminTab() {
  const [summary, setSummary] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/silica-targets/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load monthly targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = summary?.rows || [];
  const dirtyCount = Object.keys(edits).length;

  const handleEdit = (ym, value) => setEdits((prev) => ({ ...prev, [ym]: value }));

  const saveAll = async () => {
    const targets = Object.entries(edits)
      .map(([year_month, raw]) => ({
        year_month,
        target_tons: raw === '' ? 0 : parseFloat(raw),
      }))
      .filter((t) => Number.isFinite(t.target_tons) && t.target_tons >= 0);
    if (targets.length === 0) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/silica-targets`,
        { targets },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saved ${data.count} monthly target${data.count > 1 ? 's' : ''}`);
      await fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      const year = r.year_month.slice(0, 4);
      if (!out[year]) out[year] = [];
      out[year].push(r);
    }
    return out;
  }, [rows]);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="targets-admin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Monthly Silica Targets</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Set monthly tons targets from {summary?.window_start} to {summary?.window_end} (5-year window).
          </p>
        </div>
        <Button
          onClick={saveAll}
          disabled={saving || dirtyCount === 0}
          data-testid="admin-btn-save-targets"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save {dirtyCount > 0 ? `(${dirtyCount})` : ''}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-targets-table">
            <thead className="bg-slate-50 text-slate-600 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Month</th>
                <th className="text-right px-4 py-2 font-medium">Target Tons</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([year, monthRows]) => (
                <YearGroup key={year} year={year} monthRows={monthRows} edits={edits} onEdit={handleEdit} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function YearGroup({ year, monthRows, edits, onEdit }) {
  return (
    <>
      <tr className="bg-slate-100 border-t border-slate-200">
        <td colSpan={2} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">
          {year}
        </td>
      </tr>
      {monthRows.map((r) => {
        const editing = Object.prototype.hasOwnProperty.call(edits, r.year_month);
        const value = editing ? edits[r.year_month] : (r.target_tons ?? '');
        const [yyyy, mm] = r.year_month.split('-');
        const labelDate = new Date(Number(yyyy), Number(mm) - 1, 1);
        return (
          <tr key={r.year_month} className="border-t border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-2 font-mono text-xs">
              {labelDate.toLocaleString('en-IN', { month: 'short', year: 'numeric' })}
            </td>
            <td className="px-4 py-2 text-right">
              <Input
                type="number"
                step="0.01"
                min="0"
                className="h-8 w-32 ml-auto text-right"
                value={value === null || value === undefined ? '' : value}
                onChange={(e) => onEdit(r.year_month, e.target.value)}
                placeholder="—"
                data-testid={`admin-target-input-${r.year_month}`}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
}
